import { getSmartCollection } from 'api/product/getSmartCollection';
import { SmartCollectionItem, SmartCollectionResponse } from 'api/product/getSmartCollection/types';
import { useSavingsPlan } from 'hooks/savings-plan/use-savings-plan';
import useTest10173 from 'hooks/test/use-test-10173';
import { InputProductType } from 'interfaces/api-input/input-product-type';
import { ProductType } from 'interfaces/product';
import { useMemo } from 'react';
import { isEmptyProfile } from 'stores/pet-profile-new/utils/is-empty-profile';
import { useSavingPlanStore } from 'stores/saving-plan';
import { useRootStore } from 'stores/use-root-store';
import useSWR from 'swr';
import { generateInputFromPetProfile } from 'utils/api/smartCollection';
import { isPinnedUpsellProduct } from 'utils/product/is-pinned-upsell-product';
import { isProfileValidForSmartCollection } from 'stores/pet-profile-new/utils/is-profile-valid-for-smart-collection';

const PRICE_LIMIT = 25;

interface UseCrossSellSnacksReturn {
  rawResponse: SmartCollectionResponse | null | undefined;
  data: SmartCollectionItem[] | undefined;
  dislikedProteinTags: ReturnType<typeof generateInputFromPetProfile>['disliked_protein_tags'];
  includeDislikedProteins: boolean;
  isPickyCatsAssignmentPending: boolean;
  isLoading: boolean;
  error: Error | undefined;
}

interface UseCrossSellSnacksArgs {
  skip?: boolean;
  foodTypes?: InputProductType[];
}

/**
 * Hook to fetch personalized smart collection for snacks
 */
export const useCrossSellSnacks = ({
  skip,
  foodTypes = [InputProductType.Snacks, InputProductType.FoodSupplement],
}: UseCrossSellSnacksArgs): UseCrossSellSnacksReturn => {
  const petProfile = useRootStore((store) => store.currentPetProfile);
  const { isLoading: isSavingsPlanLoading } = useSavingsPlan();
  const activePlan = useSavingPlanStore((state) => state.activePlan);
  const { isPickyCatsEnabled, loading: isPickyCatsAssignmentLoading } = useTest10173();
  const { disliked_protein_tags: mappedDislikedProteinTags, ...mappedInput } =
    generateInputFromPetProfile({
      petProfile,
      includeDislikedProteins: isPickyCatsEnabled || isPickyCatsAssignmentLoading,
      omitFoodType: true,
    });
  const requestInput = {
    ...mappedInput,
    ...(isPickyCatsEnabled &&
      mappedDislikedProteinTags && { disliked_protein_tags: mappedDislikedProteinTags }),
    food_types: foodTypes,
    limit: false,
  };
  const dislikedProteinTags = isPickyCatsEnabled ? mappedDislikedProteinTags : undefined;
  const isPickyCatsAssignmentPending =
    isPickyCatsAssignmentLoading && Boolean(mappedDislikedProteinTags?.length);
  // wait until pet profile and savings plan finish their loading
  const baseKey =
    !skip &&
    !isEmptyProfile(petProfile) &&
    isProfileValidForSmartCollection(petProfile) &&
    !isSavingsPlanLoading &&
    !isPickyCatsAssignmentPending
      ? JSON.stringify(['useCrossSellSnacks', requestInput, activePlan?.ruleId])
      : null;

  const {
    data: smartCollectionData,
    isLoading,
    error,
  } = useSWR(baseKey, async () => {
    try {
      return await getSmartCollection(requestInput, activePlan);
    } catch (error) {
      console.error('❌ CrossSellPDP - Smart collection fetch error:', error);
      return null;
    }
  });

  const data = useMemo(() => {
    return smartCollectionData?.items.filter((item) => {
      if (isPinnedUpsellProduct(item.product_id)) return true;
      // PT-9018: Supplements should not be filtered by price limit
      if (item.product_category === ProductType.FoodSupplement) return true;
      const price = parseFloat(item.price);
      return price <= PRICE_LIMIT;
    });
  }, [smartCollectionData?.items]);

  return {
    error,
    rawResponse: smartCollectionData,
    data,
    dislikedProteinTags,
    includeDislikedProteins: isPickyCatsEnabled,
    isPickyCatsAssignmentPending,
    isLoading: isLoading || isSavingsPlanLoading || isPickyCatsAssignmentPending,
  };
};
